import type { MixpanelService } from '~/core/service/mixpanel/mixpanel';

interface QueuedEvent {
	type: 'track' | 'register' | 'track_pageview';
	args: unknown[];
}

// Queue to store events before Mixpanel is initialized
const eventQueue: QueuedEvent[] = [];
let isProcessingQueue = false;

/**
 * Exit before the SDK initialized: flushes the queued track events straight
 * to the ingestion API via sendBeacon, which outlives the tab. Anonymous
 * (no distinct_id — the SDK never ran), but counting the events beats
 * losing the visit. Registered only on shorts entries, where init waits for
 * the video's first frame.
 */
export function registerMixpanelExitBeacon(
	token: string,
	apiHost?: string
): void {
	if (!import.meta.client || !('sendBeacon' in navigator)) return;
	let sent = false;

	window.addEventListener('pagehide', () => {
		if (sent) return;
		const pending = eventQueue.filter(e => e.type === 'track');
		if (pending.length === 0) return;
		sent = true;
		try {
			const now = Math.floor(Date.now() / 1000);
			const events = pending.map(e => {
				const [eventName, properties] = e.args as [
					string,
					Record<string, unknown>?
				];
				return {
					event: eventName,
					properties: { ...(properties ?? {}), token, time: now }
				};
			});
			const data = btoa(unescape(encodeURIComponent(JSON.stringify(events))));
			const host = (apiHost ?? 'https://api-js.mixpanel.com').replace(/\/$/, '');
			// POST com corpo form-encoded — o formato que o SDK usa, entao
			// funciona igual no api-js.mixpanel.com e atras do proxy.
			navigator.sendBeacon(
				host + '/track/?ip=1',
				new Blob(['data=' + encodeURIComponent(data)], {
					type: 'application/x-www-form-urlencoded'
				})
			);
			// Remove apenas o que foi enviado: register/track_pageview na fila
			// sobrevivem para um retorno via bfcache.
			for (const e of pending) {
				const idx = eventQueue.indexOf(e);
				if (idx !== -1) eventQueue.splice(idx, 1);
			}
		} catch {
			// Best-effort flush — never interfere with the unload.
		}
	});
}

/**
 * Safe Mixpanel composable that handles deferred initialization.
 * Events are queued if Mixpanel is not yet loaded and processed once it's ready.
 */
export function useMixpanel() {
	const nuxtApp = useNuxtApp();

	const getMixpanel = (): MixpanelService | undefined => {
		return nuxtApp.$mixpanel as MixpanelService | undefined;
	};

	const processQueue = () => {
		if (isProcessingQueue) return;

		const mixpanel = getMixpanel();
		if (!mixpanel?.isInitialized()) return;

		isProcessingQueue = true;

		while (eventQueue.length > 0) {
			const event = eventQueue.shift();
			if (!event) continue;

			try {
				if (event.type === 'track') {
					const [eventName, properties] = event.args as [
						string,
						Record<string, unknown>?
					];
					mixpanel.track(eventName, properties);
				} else if (event.type === 'register') {
					const [props, days] = event.args as [
						Record<string, unknown>,
						number?
					];
					mixpanel.register(props, days);
				} else if (event.type === 'track_pageview') {
					const [properties, options] = event.args as [
						Record<string, unknown>?,
						{ event_name?: string }?
					];
					mixpanel.trackPageview(properties, options);
				}
			} catch (error) {
				console.error('Failed to process queued Mixpanel event:', error);
			}
		}

		isProcessingQueue = false;
	};

	// Try to process queue when called
	if (import.meta.client) {
		processQueue();
	}

	return {
		/**
		 * Track an event. Safe to call even before Mixpanel is initialized.
		 */
		track(eventName: string, properties?: Record<string, unknown>) {
			const mixpanel = getMixpanel();

			if (mixpanel?.isInitialized()) {
				mixpanel.track(eventName, properties);
			} else {
				// Queue the event for later
				eventQueue.push({ type: 'track', args: [eventName, properties] });

				// Try to process queue after a delay
				if (import.meta.client) {
					setTimeout(processQueue, 3000);
				}
			}
		},

		/**
		 * Track a Mixpanel auto page view event. Safe to call before Mixpanel is initialized.
		 */
		trackPageview(
			properties?: Record<string, unknown>,
			options?: { event_name?: string }
		) {
			const mixpanel = getMixpanel();

			if (mixpanel?.isInitialized()) {
				mixpanel.trackPageview(properties, options);
			} else {
				eventQueue.push({
					type: 'track_pageview',
					args: [properties, options]
				});

				if (import.meta.client) {
					setTimeout(processQueue, 3000);
				}
			}
		},

		/**
		 * Register super properties. Safe to call even before Mixpanel is initialized.
		 */
		register(props: Record<string, unknown>, days?: number) {
			const mixpanel = getMixpanel();

			if (mixpanel?.isInitialized()) {
				mixpanel.register(props, days);
			} else {
				eventQueue.push({ type: 'register', args: [props, days] });

				if (import.meta.client) {
					setTimeout(processQueue, 3000);
				}
			}
		},

		/**
		 * Get anonymous ID. Returns null if Mixpanel is not yet initialized.
		 */
		getAnonymousId(): string | null {
			return getMixpanel()?.getAnonymousId() ?? null;
		},

		/**
		 * Alias user. Only works if Mixpanel is initialized.
		 */
		aliasUser(userId: string, anonymousId?: string) {
			const mixpanel = getMixpanel();
			if (mixpanel?.isInitialized()) {
				mixpanel.aliasUser(userId, anonymousId);
			}
		},

		/**
		 * Check if Mixpanel is initialized.
		 */
		isInitialized(): boolean {
			return getMixpanel()?.isInitialized() ?? false;
		},

		/**
		 * Get the raw Mixpanel service. May be undefined if not yet loaded.
		 */
		get instance(): MixpanelService | undefined {
			return getMixpanel();
		}
	};
}
